import React from 'react';
import { NodeProps, Node } from '@xyflow/react';
import { Link } from 'react-router-dom';
import { MilestoneMap1, MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { Objective1 } from '@mm-shared/types/Objective1';
import { Result1 } from '@mm-shared/types/Result1';
import { GRID_CONFIG } from '../gridConfig';
import { isParentInMap } from '../../utils/objective-utils';
import { StatusCell2Presenter } from '../../components_maps/stable/StatusCell2';
import { Map3UISettings } from '../types';
import {
  getStatusCellState,
  getStatusForAdjustedInterval,
  filterItemsForInterval,
} from '../../utils/statusUtils';
import type { ColumnInfo } from '../utils/layoutMath';
import { getLocalXForColumn, isStatusColumn } from '../utils/layoutMath';
import { formatIntervalDate } from '../../utils/formatDate';
import { LinkIcon } from '@heroicons/react/24/outline';
import { UserMapCollapseControls } from './FullyCollapsedMapsGroup';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { CollapseIcon } from '../../components_misc/CollapseIcon';
import { ExpandIcon } from '../../components_misc/ExpandIcon';
import { MapTitleBar } from './MapTitleBar';

export interface MapNodeData extends Record<string, unknown> {
  milestoneMap: MilestoneMap1;
  objectives: Objective1[];
  columnsInfo: ColumnInfo[];
  settings?: Map3UISettings;
  zoomLevel?: MilestoneMapInterval;
  collapseLevel: 'expanded' | 'titleOnly' | 'fullyCollapsed';
  hideRowsWithoutDependencies: boolean;
  heightByRows: number;
  isMainMap?: boolean; // Main map cannot be fullyCollapsed
  updateMapState?: (mapUuid: string, updates: UserMapCollapseControls) => void;
}

export type CustomMapNode = Node<MapNodeData>;

/**
 * MapNode Component
 *
 * A React Flow group node that replicates Map3StaticBackground structure exactly.
 * This serves as a parent container for result nodes using the sub-flows pattern.
 *
 * This component maintains the exact same visual structure as Map3StaticBackground:
 * - Title row with map name
 * - Date headers and interval headers
 * - Objectives column with proper indentation
 * - Status cells grid with proper styling
 */
export function MapNode(props: NodeProps<CustomMapNode>) {
  const { data, selected } = props;
  const {
    milestoneMap,
    objectives,
    columnsInfo,
    settings = {},
    zoomLevel,
    collapseLevel,
    hideRowsWithoutDependencies,
    heightByRows,
    updateMapState,
  } = data;

  // Use provided zoomLevel or fall back to the column's interval type
  const effectiveZoomLevel =
    zoomLevel || columnsInfo[0]?.columnIntervalUnit || milestoneMap.interval;
  const title = milestoneMap.title;

  // Get current state for this map (now embedded in data)
  const currentMapState = {
    collapseLevel,
    hideRowsWithoutDependencies,
    heightByRows,
  };

  // Function to format the header text based on interval type (copied from Map3StaticBackground)
  const getHeaderText = (interval: MilestoneMapInterval) => {
    switch (interval) {
      case 'hours':
        return 'Hour';
      case 'days':
        return 'Day';
      case 'weeks':
        return 'Week\nEnding';
      case 'months':
        return 'Month';
      case 'quarters':
        return 'Quarter';
      case 'years':
        return 'Year';
      default:
        return 'Week\nEnding On';
    }
  };

  // Toolbar event handlers

  // Separate handlers for collapse and expand actions
  const handleCollapseClick = () => {
    console.log(`Collapse clicked for map: ${getShortIdFromUuid(milestoneMap.uuid)}`);
    let newCollapseLevel: 'expanded' | 'titleOnly' | 'fullyCollapsed';

    if (data.isMainMap) {
      // Main map: can only collapse to titleOnly
      newCollapseLevel = 'titleOnly';
    } else {
      // Related maps: collapse one level further
      switch (currentMapState.collapseLevel) {
        case 'expanded':
          newCollapseLevel = 'titleOnly';
          break;
        case 'titleOnly':
          newCollapseLevel = 'fullyCollapsed';
          break;
        case 'fullyCollapsed':
        default:
          // Already fully collapsed, no further collapse possible
          return;
      }
    }

    if (updateMapState) {
      updateMapState(milestoneMap.uuid, {
        collapseLevel: newCollapseLevel,
      });
    }
  };

  const handleExpandClick = () => {
    console.log(`Expand clicked for map: ${getShortIdFromUuid(milestoneMap.uuid)}`);
    let newCollapseLevel: 'expanded' | 'titleOnly' | 'fullyCollapsed';

    // Expand one level for both main and related maps
    switch (currentMapState.collapseLevel) {
      case 'fullyCollapsed':
        newCollapseLevel = 'titleOnly';
        break;
      case 'titleOnly':
        newCollapseLevel = 'expanded';
        break;
      case 'expanded':
      default:
        // Already fully expanded, no further expansion possible
        return;
    }

    if (updateMapState) {
      updateMapState(milestoneMap.uuid, {
        collapseLevel: newCollapseLevel,
      });
    }
  };

  // Layout calculations (copied exactly from Map3StaticBackground)
  /*
   * TABLE LAYOUT DIAGRAM
   *
   * ┌─────────────────── mapWidth ──────────────────┐
   * │                                               │
   * │ ┌─────────────── Title Row ──────────────┐    │ ← cellHeight
   * │ │              Map Title                 │    │
   * │ └────────────────────────────────────────┘    │ ← headersRowTop
   * │ ┌─objectiveColumnWidth─┐ ┌─ Status Headers ─┐ │ ← cellHeight
   * │ │    Week Ending       │ │ Jan │ Feb │ Mar  │ │ │ (2 rows tall)
   * │ └──────────────────────┘ │     │     │      │ │ ← subheadersRowTop
   * │ ┌─objectiveColumnWidth─┐ │     │     │      │ │ ← cellHeight
   * │ │     Objectives       │ │     │     │      │ │
   * │ └──────────────────────┘ └─────┴─────┴───-──┘ │ ← objectivesStartTop
   * │ ┌─objectiveColumnWidth─┐ ┌─ Status Cells ───┐ │ ← cellHeight (per row)
   * │ │   Objective 1        │ │ ■ │ ■ │ ■ │      │ │
   * │ ├──────────────────────┤ ├───┼───┼───┤      │ │
   * │ │   Objective 2        │ │ ■ │ ■ │ ■ │      │ │
   * │ ├──────────────────────┤ ├───┼───┼───┤      │ │
   * │ │   Objective 3        │ │ ■ │ ■ │ ■ │      │ │
   * │ └──────────────────────┘ └──────────────────┘ │ ← spacerRowTop
   * │ ┌─────────── Spacer Row ──────────────────┐   │ ← GRID_CONFIG.cellHeight
   * │ │                                         │   │
   * │ └─────────────────────────────────────────┘   │
   * └───────────────────────────────────────────────┘
   *
   * Background: objectiveColumnHeight (covers left column entirely)
   */
  const mapWidth =
    GRID_CONFIG.objectiveWidth +
    columnsInfo.filter((col) => isStatusColumn(col)).reduce((sum, col) => sum + col.width, 0) -
    1;

  // Use dynamic height based on collapse state (from heightByRows)
  const dynamicHeight = heightByRows * GRID_CONFIG.cellHeight;

  // Legacy calculation for expanded state positioning (still needed for internal layout)
  const objectiveColumnHeight =
    GRID_CONFIG.headerHeight + GRID_CONFIG.cellHeight + objectives.length * GRID_CONFIG.cellHeight;
  const headersRowTop = GRID_CONFIG.cellHeight;
  const subheadersRowTop = GRID_CONFIG.cellHeight + GRID_CONFIG.cellHeight;
  const objectivesStartTop = GRID_CONFIG.headerHeight + GRID_CONFIG.cellHeight;
  const spacerRowTop =
    GRID_CONFIG.headerHeight + GRID_CONFIG.cellHeight + objectives.length * GRID_CONFIG.cellHeight;
  const objectiveColumnWidth = GRID_CONFIG.objectiveWidth - 1;
  const cellHeight = GRID_CONFIG.cellHeight - 1;

  return (
    <div
      className="react-flow__pane"
      style={{
        width: `${mapWidth}px`,
        height: `${dynamicHeight}px`, // Use dynamic height that respects collapse state
        position: 'relative',
      }}
    >
      {/* Solid white background for the objectives column - only show when expanded */}
      {collapseLevel === 'expanded' && (
        <div
          className="bg-white absolute"
          style={{
            left: 0,
            top: 0,
            width: GRID_CONFIG.objectiveWidth,
            height: objectiveColumnHeight, // Use full height only when expanded
            zIndex: 0,
          }}
        />
      )}

      {/* Title Row: spans full width - ALWAYS SHOWN 
          
          UX FIX: React Flow edges display above everything else (high z-index ~2000+).
          This makes collapse/expand buttons hard to click when edges cross over them.
          
          SOLUTION: MapTitleBar component positioned above edges
          - MapTitleBar z-index: 40 (above edges at 30)
          - Ensures all title bar content (buttons, links) is clickable above edges
      */}
      <div className="title-row absolute top-0 left-0 right-0 bg-white">
        <MapTitleBar
          milestoneMap={milestoneMap}
          collapseLevel={collapseLevel}
          isMainMap={data.isMainMap}
          onCollapseClick={handleCollapseClick}
          onExpandClick={handleExpandClick}
          width={mapWidth}
          height={cellHeight}
          variant="react-component"
          containerZIndex={2}
          buttonZIndex={GRID_CONFIG.zIndex.edges + 10} // 40 - above edges but below other UI
        />
      </div>

      {/* Everything below title bar - ONLY shown when expanded */}
      {collapseLevel === 'expanded' && (
        <>
          {/* Headers Row: interval header and date headers */}
          <div
            className="headers-row absolute bg-white left-0 right-0"
            style={{ top: headersRowTop, zIndex: 1 }}
          >
            {/* Interval Header (1 row tall) */}
            <div
              className="objective-header absolute flex items-center justify-end bg-white pr-4 font-bold text-right border-l border-slate-400"
              style={{
                top: 0,
                left: 0,
                width: objectiveColumnWidth,
                height: cellHeight,
                zIndex: 2,
              }}
            >
              <div style={{ whiteSpace: 'pre-line' }}>{getHeaderText(effectiveZoomLevel)}</div>
            </div>

            {/* Date Headers (2 rows tall, starting here) */}
            {columnsInfo.map((column, index) => {
              if (!isStatusColumn(column)) return null;
              const x = getLocalXForColumn(column);

              const statusColumns = columnsInfo.filter((col) => isStatusColumn(col));
              const isLastStatusColumn = column === statusColumns[statusColumns.length - 1];

              return (
                <div
                  key={index}
                  className={`absolute px-2 text-center flex items-start justify-center bg-white font-bold ${
                    isLastStatusColumn ? 'border-r border-slate-400' : ''
                  }`}
                  style={{
                    left: x,
                    width: column.width - 1,
                    height: GRID_CONFIG.headerHeight - 1, // 2 rows tall
                    zIndex: 2,
                  }}
                >
                  <div>{formatIntervalDate(column.endDate, effectiveZoomLevel)}</div>
                </div>
              );
            })}
          </div>

          {/* Objectives Heading */}
          <div
            className="subheaders-row absolute bg-white left-0 right-0"
            style={{ top: subheadersRowTop, zIndex: 1 }}
          >
            <div
              className="absolute px-2 flex items-center bg-white font-bold border-l border-slate-400"
              style={{
                left: 0,
                width: objectiveColumnWidth,
                height: cellHeight,
                zIndex: 2,
              }}
            >
              <div>Objectives</div>
            </div>
          </div>

          {/* Objective Column */}
          <div className="objectives-column absolute">
            {objectives.map((objective, index) => {
              const isParent = isParentInMap(objective.uuid, milestoneMap);
              return (
                <div
                  key={objective.uuid}
                  className={`absolute px-2 bg-white text-sm flex items-center gap-2 min-h-8 border-l border-slate-400 ${
                    isParent ? 'font-semibold' : ''
                  }`}
                  style={{
                    top: objectivesStartTop + index * GRID_CONFIG.cellHeight,
                    left: 0,
                    width: objectiveColumnWidth,
                    height: cellHeight,
                    zIndex: 2,
                    paddingLeft: isParent ? '0.5rem' : '2rem',
                  }}
                >
                  <div className="truncate">{objective.text || `Objective ${index + 1}`}</div>
                </div>
              );
            })}
          </div>

          {/* Status Cells */}
          <div className="status-cells absolute">
            {objectives.map((objective, rowIndex) =>
              columnsInfo.map((column, colIndex) => {
                if (!isStatusColumn(column)) return null;

                const status = getStatusForAdjustedInterval(
                  objective.statuses || [],
                  { start: column.startDate, end: column.endDate },
                  milestoneMap.interval,
                  effectiveZoomLevel,
                );

                const { hasResults, hasKeyResults, hasComments } = getStatusCellState(objective, {
                  start: column.startDate,
                  end: column.endDate,
                });

                const x = getLocalXForColumn(column);

                const statusColumns = columnsInfo.filter((col) => isStatusColumn(col));
                const isLastStatusColumn = column === statusColumns[statusColumns.length - 1];

                return (
                  <div
                    className={isLastStatusColumn ? 'border-r border-slate-400' : ''}
                    key={`${objective.uuid}-${column.endDate}`}
                    style={{
                      position: 'absolute',
                      top: objectivesStartTop + rowIndex * GRID_CONFIG.cellHeight,
                      left: x,
                      width: column.width - 1,
                      height: cellHeight,
                      zIndex: 0,
                    }}
                  >
                    <StatusCell2Presenter
                      objectiveIsParent={isParentInMap(objective.uuid, milestoneMap)}
                      statusText={status?.statusCode || ''}
                      statusCode={status?.statusCode || ''}
                      hasResults={hasResults}
                      hasKeyResults={hasKeyResults}
                      hasComments={hasComments}
                      resultsForInterval={filterItemsForInterval<Result1>(
                        objective.results || [],
                        column.startDate,
                        column.endDate,
                      )}
                      cellRef={{ current: null }}
                      handleClick={() => {}}
                      handleKeyDown={() => {}}
                      handleFocus={() => {}}
                      handleBlur={() => {}}
                      showStatusCodes={settings.showStatusCodes ?? false}
                      statusUuid={status?.uuid || ''}
                      objectiveUuid={objective.uuid}
                      row={rowIndex}
                      column={colIndex + 1}
                      forIntervalEnding={column.endDate.toISOString()}
                      milestoneMapUuid={milestoneMap.uuid}
                      activateTooltip={false}
                      focusPosition={{ row: -1, col: -1 }}
                      hoverPosition={{ row: -1, col: -1 }}
                      enableCrosshairs={settings.enableCrosshairs ?? false}
                      enableResultCircleFill={true}
                      showResultCircles={false}
                    />
                  </div>
                );
              }),
            )}
          </div>

          {/* Spacer Row at the bottom */}
          <div
            className="absolute left-0 border-t border-slate-400"
            style={{
              top: spacerRowTop,
              width: mapWidth,
              height: GRID_CONFIG.cellHeight,
              zIndex: 1,
            }}
          />
        </>
      )}
    </div>
  );
}

// Export the node type for registration
export const mapNodeTypes = {
  mapNode: MapNode,
};
